import { createSlice } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { DEFAULT_LANGUAGE } from '../translations';

dayjs.extend(utc);

/** Map a picker/local calendar date onto that same YYYY-MM-DD in UTC, not the UTC instant. */
function toUtcDayStart(value) {
  return dayjs.utc(value.format('YYYY-MM-DD')).startOf('day');
}

function toUtcDayEnd(value) {
  return dayjs.utc(value.format('YYYY-MM-DD')).endOf('day');
}

const getTodayKey = () => dayjs().format('YYYY-MM-DD');
const getFromValueInitial = () => dayjs.utc(getTodayKey()).startOf('day');
const getToValueInitial = () => dayjs.utc(getTodayKey()).endOf('day');
const getDateAnchorInitial = () => dayjs().startOf('day');

export const appSlice = createSlice({
  name: 'app',
  initialState: {
    // accounts
    accounts: [],
    account: null,

    // sites
    sites: [],
    site: 'All',
    siteId: undefined,
    allSites: ['All'],

    // date range
    fromValue: getFromValueInitial(),
    toValue: getToValueInitial(),
    datePeriod: 'day',
    dateAnchor: getDateAnchorInitial(),

    // permissions
    permissionLevel: 'Admin',
    availableActions: [],
    allowedReportKeys: [],
    reportKeysLoaded: false,

    // language
    language: localStorage.getItem('language') || DEFAULT_LANGUAGE,
  },
  reducers: {
    // accounts
    setAccounts: (state, action) => {
      state.accounts = action.payload.accounts;
    },
    setAccount: (state, action) => {
      state.account = action.payload.account;
      // Clear site state when account is cleared (e.g., during switch account)
      if (!action.payload.account) {
        state.site = 'All';
        state.siteId = undefined;
        state.sites = [];
        state.allSites = ['All'];
        state.availableActions = [];
        state.allowedReportKeys = [];
        state.reportKeysLoaded = false;
      }
    },

    // sites
    setSites: (state, action) => {
      state.sites = action.payload.sites;
    },
    changeSite: (state, action) => {
      state.site = action.payload.newSite;
      state.siteId = action.payload.siteId ?? null;
    },
    updateSites: (state, action) => {
      state.allSites = action.payload;
    },

    // date range
    setFromValue: (state, action) => {
      state.fromValue = toUtcDayStart(action.payload);
    },
    setToValue: (state, action) => {
      state.toValue = toUtcDayEnd(action.payload);
    },
    setDatePeriod: (state, action) => {
      state.datePeriod = action.payload;
    },
    setDateAnchor: (state, action) => {
      state.dateAnchor = action.payload.startOf('day');
    },

    // permissions
    updatePermissions: (state, action) => {
      state.permissionLevel = action.payload;
    },

    setAvailableActions: (state, action) => {
      state.availableActions = action.payload;
    },

    setAllowedReportKeys: (state, action) => {
      state.allowedReportKeys = action.payload;
      state.reportKeysLoaded = true;
    },

    // language
    setLanguage: (state, action) => {
      state.language = action.payload;
      localStorage.setItem('language', action.payload);
    },
  },
});

export const { updatePermissions, setFromValue, setToValue, setDatePeriod, setDateAnchor, setAccounts, setAccount, setSites, changeSite, updateSites, setAvailableActions, setAllowedReportKeys, setLanguage } = appSlice.actions;

export default appSlice.reducer;
